import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Button,
  Divider,
  Tooltip,
  CircularProgress,
  alpha,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import CompareArrowsIcon  from "@mui/icons-material/CompareArrows";
import CloseIcon          from "@mui/icons-material/Close";

import DualBoxSection from "./components/DualBoxSection";
import { buildRowExistenceDiff, getColumnsFromDict } from "./utils/tableHelpers";

export default function MaterialsTab({
  mode = "bulk",
  leftData,
  rightData,
  setLeftDict:  setLeftDictExternal,
  setRightDict: setRightDictExternal,
  removeLeftTable,
  removeRightTable,
  isLoading = false,
}) {
  const theme  = useTheme();
  const isBulk = mode === "bulk";

  /* ── Local dict state ── */
  const [leftDict,  setLeftDict]  = useState(leftData  || {});
  const [rightDict, setRightDict] = useState(rightData || {});

  useEffect(() => setLeftDict(leftData   || {}), [leftData]);
  useEffect(() => setRightDict(rightData || {}), [rightData]);

  /* ── Row selection (bulk only) ── */
  const [selectedLeftRow,  setSelectedLeftRow]  = useState(null);
  const [selectedRightRow, setSelectedRightRow] = useState(null);

  /* ── Compare ── */
  const [compareMode,   setCompareMode]   = useState(false);
  const [isComparing,   setIsComparing]   = useState(false);
  const [leftDiffMap,   setLeftDiffMap]   = useState({});
  const [rightDiffMap,  setRightDiffMap]  = useState({});

  /* ── Editing ── */
  const handleLeftEdit  = (rowId, field, val) =>
    setLeftDict((p)  => ({ ...p,  [rowId]: { ...p[rowId],  [field]: val } }));
  const handleRightEdit = (rowId, field, val) =>
    setRightDict((p) => ({ ...p,  [rowId]: { ...p[rowId],  [field]: val } }));

  /* ── Compare ── */
  async function handleCompare() {
    setIsComparing(true);
    await new Promise((r) => setTimeout(r, 30));
    const { leftMap, rightMap } = buildRowExistenceDiff(leftDict, rightDict);
    setLeftDiffMap(leftMap);
    setRightDiffMap(rightMap);
    setCompareMode(true);
    setIsComparing(false);
  }

  function handleClearCompare() {
    setCompareMode(false);
    setLeftDiffMap({});
    setRightDiffMap({});
  }

  /* ── Bulk row actions ── */
  function moveLeftRow() {
    if (!selectedLeftRow) return;
    setRightDict((p) => ({ ...p, [selectedLeftRow]: leftDict[selectedLeftRow] }));
    setLeftDict((p)  => { const n = { ...p }; delete n[selectedLeftRow]; return n; });
    setSelectedLeftRow(null);
  }
  function moveRightRow() {
    if (!selectedRightRow) return;
    setLeftDict((p)  => ({ ...p, [selectedRightRow]: rightDict[selectedRightRow] }));
    setRightDict((p) => { const n = { ...p }; delete n[selectedRightRow]; return n; });
    setSelectedRightRow(null);
  }
  function removeLeftRow() {
    if (!selectedLeftRow) return;
    setLeftDict((p) => { const n = { ...p }; delete n[selectedLeftRow]; return n; });
    setSelectedLeftRow(null);
  }
  function removeRightRow() {
    if (!selectedRightRow) return;
    setRightDict((p) => { const n = { ...p }; delete n[selectedRightRow]; return n; });
    setSelectedRightRow(null);
  }

  /* ── Shared button style ── */
  const outlineBtn = (color) => ({
    fontSize: "0.7rem", fontWeight: 600, textTransform: "none", borderRadius: 1.5,
    borderColor: alpha(color, 0.3), color,
    "&:hover": { borderColor: color, backgroundColor: alpha(color, 0.05) },
    "&.Mui-disabled": { borderColor: alpha(color, 0.12), color: alpha(color, 0.3) },
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>

      {/* ── Toolbar ── */}
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        flexWrap="wrap"
        sx={{
          px: 1.25, py: 0.85, borderRadius: 1.5,
          backgroundColor: alpha(theme.palette.primary.main, 0.06),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.20)}`,
          gap: 0.75,
        }}
      >
        {/* Loading pulse */}
        {isLoading && (
          <CircularProgress size={13} thickness={5}
            sx={{ color: theme.palette.primary.main }} />
        )}

        {/* Compare / Clear Compare */}
        {!compareMode ? (
          <Button
            size="small" variant="outlined"
            startIcon={
              isComparing
                ? <CircularProgress size={11} thickness={5} sx={{ color: "inherit" }} />
                : <CompareArrowsIcon sx={{ fontSize: "13px !important" }} />
            }
            onClick={handleCompare}
            disabled={isComparing || isLoading || Object.keys(leftDict).length === 0 || Object.keys(rightDict).length === 0}
            sx={outlineBtn(theme.palette.primary.main)}
          >
            {isComparing ? "Comparing…" : "Compare"}
          </Button>
        ) : (
          <Button
            size="small" variant="contained" disableElevation
            startIcon={<CloseIcon sx={{ fontSize: "13px !important" }} />}
            onClick={handleClearCompare}
            sx={{
              fontSize: "0.7rem", fontWeight: 600, textTransform: "none", borderRadius: 1.5,
              backgroundColor: alpha(theme.palette.error.main, 0.82),
              "&:hover": { backgroundColor: theme.palette.error.main },
            }}
          >
            Clear Compare
          </Button>
        )}


      </Stack>

      {/* ── Tables (stacked vertically) ── */}
      <DualBoxSection
        leftDict={leftDict}
        rightDict={rightDict}
        leftColumns={getColumnsFromDict(leftDict, mode)}
        rightColumns={getColumnsFromDict(rightDict, mode)}
        onLeftRowSelect={isBulk ? setSelectedLeftRow  : undefined}
        onRightRowSelect={isBulk ? setSelectedRightRow : undefined}
        selectedLeftRow={selectedLeftRow}
        selectedRightRow={selectedRightRow}
        onLeftEdit={handleLeftEdit}
        onRightEdit={handleRightEdit}
        compareMode={compareMode}
        leftDiffMap={leftDiffMap}
        rightDiffMap={rightDiffMap}
        removeLeftTable={removeLeftTable}
        removeRightTable={removeRightTable}
      />
    </Box>
  );
}
